import { Injectable } from "@angular/core";
import { HttpClient } from "../shared/services/http/http-client.service";
import { throwError as observableThrowError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { DriverNote } from "@app/shared/models/drivers/DriverViewModel.model";
import { ClientNoteViewModel } from "@app/shared/models/clients/ClientNoteViewModel.model";

export interface DashboardNoteItem {
  id: string;
  driverId?: string;
  driverName?: string;
  clientId?: string;
  clientName?: string;
  note?: string;
  type: string;
  status: string;
  dateCreatedUtc: Date;
  followUpDate?: Date;
  tripNumber?: string;
  customerReferenceNumber?: string;
  createdByUserName?: string;
  emailedToSafety?: Date;
  emailedFormToDriver?: Date;
  emailedToClient?: Date;
  emailedToDriver?: Date;
}

export interface PaginatedDriverNotes {
  items: DashboardNoteItem[];
  totalCount: number;
}

export interface AccidentBreakdownItem {
  id: string;
  driverId?: string;
  driverName?: string;
  type: string;
  status: string;
  dateCreatedUtc: Date;
  accidentDate?: string;
  accidentTime?: string;
  accidentCity?: string;
  accidentState?: string;
  accidentLocation?: string;
  anyInjuries?: boolean;
  anotherVehicleInvolved?: boolean;
  driverCitation?: boolean;
  citationDescription?: string;
  accidentDescription?: string;
  damageDescription?: string;
  weather?: string;
  photosToSafety?: string;
  truckTrailerIssue?: string;
  eta?: string;
  tripNumber?: string;
  roadCallNumber?: string;
  tractorNumber?: string;
  trailerNumber?: string;
  clientName?: string;
  note?: string;
  clientNotes?: string;
  emailedToSafety?: Date;
  emailedFormToDriver?: Date;
  emailedToClient?: Date;
  emailedToDriver?: Date;
}

export interface DriverListing {
  id: string;
  name: string;
  picture?: string;
}

export interface DriverStatusSummary {
  returningDrivers: DriverListing[];
  returningDriversCount: number;
  accidentBreakdownCount: number;
}

export interface PaginatedAccidentBreakdowns {
  items: AccidentBreakdownItem[];
  totalCount: number;
}

export interface PaginatedClientNotes {
  items: DashboardNoteItem[];
  totalCount: number;
}

@Injectable({
  providedIn: "root",
})
export class DispatchDashboardService {
  constructor(private _http: HttpClient) {}

  getDriverNotes(
    params: {
      includeClosed?: boolean;
      sortField?: string;
      sortDirection?: string;
      page?: number;
      pageSize?: number;
      search?: string;
      typeFilter?: string;
      clientFilter?: string;
      dateFrom?: string;
      dateTo?: string;
    } = {}
  ) {
    const queryParts: string[] = [];
    if (params.includeClosed) queryParts.push("includeClosed=true");
    if (params.sortField) queryParts.push(`sortField=${encodeURIComponent(params.sortField)}`);
    if (params.sortDirection)
      queryParts.push(`sortDirection=${encodeURIComponent(params.sortDirection)}`);
    if (params.page != null) queryParts.push(`page=${params.page}`);
    if (params.pageSize != null) queryParts.push(`pageSize=${params.pageSize}`);
    if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params.typeFilter) queryParts.push(`typeFilter=${encodeURIComponent(params.typeFilter)}`);
    if (params.clientFilter)
      queryParts.push(`clientFilter=${encodeURIComponent(params.clientFilter)}`);
    if (params.dateFrom) queryParts.push(`dateFrom=${encodeURIComponent(params.dateFrom)}`);
    if (params.dateTo) queryParts.push(`dateTo=${encodeURIComponent(params.dateTo)}`);
    const qs = queryParts.length ? "?" + queryParts.join("&") : "";
    return this._http.get(`api/DispatchDashboard/driver-notes${qs}`).pipe(
      map((res) => res as PaginatedDriverNotes),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getOpenClientNotes(
    params: {
      includeClosed?: boolean;
      sortDirection?: string;
      page?: number;
      pageSize?: number;
      search?: string;
      typeFilter?: string;
      clientFilter?: string;
      dateFrom?: string;
      dateTo?: string;
    } = {}
  ) {
    const queryParts: string[] = [];
    if (params.includeClosed) queryParts.push("includeClosed=true");
    if (params.sortDirection)
      queryParts.push(`sortDirection=${encodeURIComponent(params.sortDirection)}`);
    if (params.page != null) queryParts.push(`page=${params.page}`);
    if (params.pageSize != null) queryParts.push(`pageSize=${params.pageSize}`);
    if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params.typeFilter) queryParts.push(`typeFilter=${encodeURIComponent(params.typeFilter)}`);
    if (params.clientFilter)
      queryParts.push(`clientFilter=${encodeURIComponent(params.clientFilter)}`);
    if (params.dateFrom) queryParts.push(`dateFrom=${encodeURIComponent(params.dateFrom)}`);
    if (params.dateTo) queryParts.push(`dateTo=${encodeURIComponent(params.dateTo)}`);
    const qs = queryParts.length ? "?" + queryParts.join("&") : "";
    return this._http.get(`api/DispatchDashboard/open-client-notes${qs}`).pipe(
      map((res) => res as PaginatedClientNotes),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getAccidentsBreakdowns(
    params: {
      includeClosed?: boolean;
      sortDirection?: string;
      page?: number;
      pageSize?: number;
      search?: string;
      typeFilter?: string;
      dateFrom?: string;
      dateTo?: string;
    } = {}
  ) {
    const queryParts: string[] = [];
    if (params.includeClosed) queryParts.push("includeClosed=true");
    if (params.sortDirection)
      queryParts.push(`sortDirection=${encodeURIComponent(params.sortDirection)}`);
    if (params.page != null) queryParts.push(`page=${params.page}`);
    if (params.pageSize != null) queryParts.push(`pageSize=${params.pageSize}`);
    if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params.typeFilter) queryParts.push(`typeFilter=${encodeURIComponent(params.typeFilter)}`);
    if (params.dateFrom) queryParts.push(`dateFrom=${encodeURIComponent(params.dateFrom)}`);
    if (params.dateTo) queryParts.push(`dateTo=${encodeURIComponent(params.dateTo)}`);
    const qs = queryParts.length ? "?" + queryParts.join("&") : "";
    return this._http.get(`api/DispatchDashboard/accidents-breakdowns${qs}`).pipe(
      map((res) => res as PaginatedAccidentBreakdowns),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getFullDriverNote(noteId: string) {
    return this._http.get(`api/DispatchDashboard/driver-note/${noteId}`).pipe(
      map((res) => res as DriverNote),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getFullClientNote(noteId: string) {
    return this._http.get(`api/DispatchDashboard/client-note/${noteId}`).pipe(
      map((res) => res as ClientNoteViewModel),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDriverStatus() {
    return this._http.get("api/DispatchDashboard/driver-status").pipe(
      map((res) => res as DriverStatusSummary),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDriverCard(driverId: string) {
    return this._http.get(`api/board/driverDetail/${driverId}`).pipe(
      map((res) => res as any),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }
}
